import { memo } from 'react';
import SiteAccordion from 'components/site/site-accordion';
import NextImage from 'next/image';
import { createGlobalStyle } from 'styled-components';
import { mqSmallMin, mqLargeMin } from 'styled/tools/media-queries';

const FormulationStyles = createGlobalStyle`
  .pds08-formulation {
  max-width: none !important;
  padding: 0 !important;
  background-color: var(--fill-primary);

  > .container {
    max-width: 1440px;
    padding: 30px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;

    @media ${mqLargeMin} {
      padding: 80px 50px 100px;
      flex-direction: row;
    }
  }

  .title {
    * {
      color: var(--text-primary);
    }

    h2 {
      font-size: 36px;
      margin-bottom: 30px;

      @media ${mqLargeMin} {
        font-size: 50px;
        margin-bottom: 20px;
      }
    }

    @media ${mqLargeMin} {
      width: 300px;
      margin-right: 50px;
    }

    > div {
      margin-top: 30px;
    }
  }

  .contents {
    flex-grow: 1;
  }

  .accordion-container {
    margin: 30px 0;

    @media ${mqLargeMin} {
      margin: 0;
    }
  }

  .icon-container {
    display: flex;
    flex-wrap: wrap;
    max-width: 800px;
    gap: 24px 40px;

    @media ${mqLargeMin} {
      margin-top: 60px;
      gap: 24px 50px;
    }

    .formulation-icon {
      display: flex;
      align-items: center;
      width: 120px;

      @media ${mqSmallMin} {
        width: 140px;
        max-width: 140px;
      }

      img {
        width: 36px;
        height: 36px;
      }

      span {
        margin-left: 8px;
        font-size: 12px;
        color: var(--text-primary);
      }
    }
  }

  .site-accordion {
    .head {
      @media ${mqLargeMin} {
        padding: 12px 0;
      }
    }

    .title {
      display: flex;
      flex-grow: 1;
      justify-content: space-between;

      span {
        display: none;

        @media ${mqLargeMin} {
          display: inline;
        }
      }
    }

    .body * {
      background-color: transparent;
      color: var(--text-primary);
    }
  }
}

`;

const PDS08Formulation = () => {
  const accordionContent = [
    {
      title: 'Gastrointestinal Immunity/Health Blend º',
      quantity: '19.5 Billion AFU',
      content:
        '<i>Lacticaseibacillus rhamnosus</i> SD-GG-IT<br /><i>Lactobacillus acidophilus</i> SD-NCFM-US<br/><i>Bifidobacterium lactis</i> SD-Bi07-US<br/><i>Bifidobacterium breve</i> SD-BR3-IT',
    },
    {
      title: 'Respiratory Health Blend º',
      quantity: '3.5 Billion AFU',
      content:
        '<i>Bifidobacterium breve</i> SD-B632-IT<br /><i>Ligilactobacillus salivarius</i> SD-LS01-IT',
    },
    {
      title: 'Dermatological Health Blend º',
      quantity: '1.5 Billion AFU',
      content:
        '<i>Bifidobacterium lactis</i> SD-CECT8145-SP<br /><i>Bifidobacterium longum</i> SD-CECT7347-SP<br/><i>Lacticaseibacillus casei</i> SD-CECT9104-SP',
    },
    {
      title: 'Prebiotic MAOS Microbiota-Accessible Oligomeric Substrates™',
      quantity: '6.09g',
      content:
        'Molecular weight profiled inulin and fructooligosaccharides from chicory root.',
    },
    {
      title: 'Other Ingredients',
      quantity: '',
      content:
        'Glyceryl fatty acid esters (for microencapsulation of certain probiotic strains).',
    },
  ];

  const iconContent = [
    {
      img: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/gluten-tested.svg',
      text: 'Gluten-tested',
    },
    {
      img: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/dairy-tested.svg',
      text: 'Dairy-tested',
    },
    {
      img: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/soy-tested.svg',
      text: 'Soy-tested',
    },
    {
      img: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/nuts-tested.svg',
      text: 'Nuts-tested',
    },
    {
      img: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/sesame-tested.svg',
      text: 'Sesame-tested',
    },
    {
      img: 'https://res.cloudinary.com/dljz0lko8/image/upload/v1738635955/formulation-icons/no-sugar-added.svg',
      text: 'No Added Sugar',
    },
  ];

  return (
    <section className='pds08-formulation' id='formulation'>
      <FormulationStyles />
      <div className='container'>
        <div className='title'>
          <h2>Formulation</h2>
          <h3 className='h5-style'>
            <strong>24.5 Billion AFU</strong>
          </h3>
          <h3 className='h5-style'>
            <strong>6g molecular weighted inulin + FOS</strong>
          </h3>
          <h3 className='h5-style'>
            <strong>No refrigeration required.</strong>
          </h3>
        </div>

        <div className='contents'>
          <div className='accordion-container'>
            {accordionContent.map((item, i) => (
              <SiteAccordion
                key={`accordion-${i}`}
                index={i}
                title={`<h3>${item.title}</h3><span>${item.quantity}</span>`}
                stringContent={item.content}
              />
            ))}
          </div>
          <div className='icon-container'>
            {iconContent.map((item, i) => (
              <div key={`icon-${i}`} className='formulation-icon'>
                <NextImage
                  src={item.img}
                  alt={item.text || ''}
                  width={36}
                  height={36}
                />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(PDS08Formulation);
